import { useState } from 'react';
import { connect } from 'react-redux';
import {
  Typography,
  CircularProgress,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Autocomplete,
  TextField,
  Stack,
} from '@mui/material';

import { RemoveCircleRounded, ControlPointRounded } from '@mui/icons-material';
import { objectsEqual } from '../../../utils/comparators';
import { unique } from '../../../utils/collections';

const StateMappingSelector = ({ onCancel, entities, onSubmit, setError }) => {
  const currentStates = unique(entities.map((e) => e.data.state_text).filter(Boolean));
  const currentDefaults = unique(
    entities.flatMap((e) => Object.entries(e.defaultStateMapping))
  ).reduce((acc, [key, value]) => {
    acc[key] = acc[key] ? `${acc[key]},${value}` : value;
    return acc;
  }, {});

  const currentSuggestions = unique(
    entities.map((e) => e.data.predicted_state_text).filter(Boolean)
  );
  const [mapping, setMapping] = useState(
    Object.fromEntries(
      [currentSuggestions, currentStates].flatMap((s) => s.flatMap((p) => Object.entries(p)))
    )
  );
  const [submitting, setSubmitting] = useState('');
  const maxIndex =
    Object.keys(mapping).length !== 0 ? Math.max(...Object.keys(mapping).map(Number)) : 0;
  return (
    <>
      <DialogTitle>State Text Assignment</DialogTitle>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitting('Processing Request');
          onSubmit(mapping)
            .then(() => setSubmitting(''))
            .catch((e) => {
              setSubmitting('');
              setError(e.message);
            });
        }}
        onReset={onCancel}
        id="stateText"
        key={entities.length}
      >
        <DialogContent>
          {Object.prototype.hasOwnProperty.call(mapping, '0') ||
          Object.keys(mapping).length === 0 ? null : (
            <IconButton onClick={() => setMapping({ ...mapping, 0: '' })}>
              <ControlPointRounded color="success" />
            </IconButton>
          )}
          {Object.entries(mapping)?.map((e) => {
            const removable = e[0] === '0' || e[0] === String(maxIndex);

            return (
              <Stack key={e[0]} direction="row">
                <IconButton
                  disabled={!removable}
                  onClick={() => {
                    const { [e[0]]: _removed, ...newMapping } = mapping;
                    return setMapping(newMapping);
                  }}
                >
                  <RemoveCircleRounded
                    color="error"
                    style={{ visibility: removable ? '' : 'hidden' }}
                  />
                </IconButton>
                <TextField value={e[0]} disabled sx={{ width: '35px', margin: '10px' }} />
                <TextField value={currentDefaults[e[0]]} disabled sx={{ margin: '10px' }} />
                <Autocomplete
                  slotProps={{
                    popper: { style: { width: 'fit-content' } },
                  }}
                  options={unique(currentSuggestions.map((s) => s[e[0]]).filter(Boolean))}
                  freeSolo
                  fullWidth
                  clearOnBlur
                  openOnFocus
                  autoSelect
                  disableClearable
                  value={e[1]}
                  onChange={(event, result) => {
                    if (result) setMapping({ ...mapping, [e[0]]: result });
                  }}
                  renderInput={(params) => (
                    <TextField
                      required
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...params}
                      sx={{ margin: '10px' }}
                      name="entityName"
                    />
                  )}
                />
              </Stack>
            );
          })}
          <IconButton onClick={() => setMapping({ ...mapping, [String(maxIndex + 1)]: '' })}>
            <ControlPointRounded color="success" />
          </IconButton>
        </DialogContent>
        <DialogActions>
          <Button type="reset" form="stateText">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            type="submit"
            form="stateText"
            disabled={
              submitting ||
              objectsEqual(unique(entities.map(({ data }) => data.state_text)), [mapping])
            }
          >
            {submitting ? (
              <CircularProgress color="success" size="24px" aria-label="Processing…" />
            ) : (
              'Submit'
            )}
          </Button>
        </DialogActions>
      </form>
    </>
  );
};

const StateMappingDialog = ({
  onSubmit,
  onCancel,

  entities,
}) => {
  const [error, setError] = useState();
  return (
    <Dialog open={entities.length > 0} fullWidth>
      <StateMappingSelector
        entities={entities}
        onSubmit={onSubmit}
        onCancel={onCancel}
        setError={setError}
      />
      {error ? (
        <Typography sx={{ padding: '10px' }} color="error">
          {error.replaceAll('<br />', '\n')}
        </Typography>
      ) : null}
    </Dialog>
  );
};

export default connect()(StateMappingDialog);
